import { createContext, useContext, useLayoutEffect, useState, ReactNode } from 'react'

type Theme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    // Persisted preference wins; otherwise default to dark
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme')
      if (saved === 'light' || saved === 'dark') return saved
      return 'dark'
    }
    return 'dark'
  })

  // Keep DOM (html class + color-scheme) in sync with state.
  // useLayoutEffect avoids “stuck” styles/flash on toggle.
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return

    localStorage.setItem('theme', theme)
    const root = document.documentElement

    if (theme === 'dark') {
      root.classList.add('dark')
      root.style.colorScheme = 'dark'
    } else {
      root.classList.remove('dark')
      root.style.colorScheme = 'light'
    }
  }, [theme])

  const toggleTheme = () => {
    // Add a temporary class so all elements smoothly transition
    // background/color/border instead of snapping.
    const root = document.documentElement
    root.classList.add('theme-transitioning')
    setTheme(prev => prev === 'light' ? 'dark' : 'light')
    // Remove after the transition completes
    window.setTimeout(() => root.classList.remove('theme-transitioning'), 200)
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
